// services/customerIdentities.ts — one customer per email + phone pair, with the ID check (migration 0126, 2026-10-10;
// the website ID check, migration 0128, 2026-10-13; its three steps, migration 0131, 2026-10-14; back to two
// photos, migration 0132, 2026-10-15).
//
// The client's rule: a customer is the EMAIL + PHONE PAIR as seen on orders. `customer_identities` holds one
// row per pair (phone in the order's +1XXXXXXXXXX form, email lower-cased and trimmed, '' when the orders carry
// none), its order count and dates, and whether the customer's ID was checked: a delivered order before 0126
// ('backfill'), the first delivered order since ('auto', by the orders trigger), the button on the Order Board
// card or the review queue ('dispatcher'), or the website's own check ('ai'): the front of the ID, then a
// selfie holding a handwritten note dated today, each judged by the model, verified when the two are accepted
// (0132). Since 0128 the row also carries the website check's state: `checkStatus` (none · pending · retry ·
// needs_review · rejected), the refused photos this cycle and the last reason. Clients only read the table;
// the writes go through the RPCs below. Supabase only.
//
// The full number (0136, Umer 2026-10-18; the owner only since 0137, 2026-10-20): only the owner reads the table
// itself. The super admin's list goes through id_identities_page / id_identities_total and the review queue
// through id_review_* RPCs that answer the phone masked ("(613) ***-0142",
// `phoneMasked`) with the pair's id (`ref`), and opens, decides and lists a pair by that id; the Order Board
// reads its pairs through customer_identities_for_phones. Each of those falls back to the direct read while
// the database does not have 0136 yet (the RPC is missing), so this code can go out before the migration.
import { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { formatForDisplay, isValidPhone, toE164 } from './phone';
import { authHeader } from './smsImagesApi';

export type IdVerifiedSource = 'backfill' | 'auto' | 'dispatcher' | 'ai';
/** The website ID check's state on a pair that is not verified (0128). */
export type IdCheckStatus = 'none' | 'pending' | 'retry' | 'needs_review' | 'rejected';

export interface CustomerIdentity {
  /** E.164 (+1XXXXXXXXXX) — half of the key, read-only. "(613) ***-0142" when `phoneMasked`. */
  phone: string;
  /** The pair's id (0136): how the review queue opens and decides a pair without its phone. */
  ref?: string | null;
  /** True when `phone` is the masked form (for everyone but the owner, 0137). */
  phoneMasked?: boolean;
  /** Lower-cased, trimmed; '' when the pair's orders carry no email — the other half of the key. */
  email: string;
  /** The name on the pair's latest order. */
  name: string;
  /** Orders of the pair that are not cancelled. */
  orderCount: number;
  firstOrderAt: Date | null;
  lastOrderAt: Date | null;
  isIdVerified: boolean;
  idVerifiedAt: Date | null;
  /** users.id of the dispatcher, 'auto', 'backfill' or 'ai'. */
  idVerifiedBy: string | null;
  idVerifiedByName: string | null;
  idVerifiedSource: IdVerifiedSource | null;
  idVerifiedNote: string | null;
  /** The website ID check (0128): where it stands while the pair is not verified. */
  checkStatus: IdCheckStatus;
  /** Refused photos in the current cycle (three hand the pair to a person). */
  checkAttempts: number;
  /** The last refusal reason (a policy reason code, or ai_unavailable). */
  checkReason: string | null;
  checkCycle: number;
  checkUpdatedAt: Date | null;
  updatedAt: Date | null;
}

/** One of the pair's orders, for the side panel. */
export interface IdentityOrder {
  id: string;
  orderNumber: string;
  status: string;
  createdAt: Date | null;
  deliveredAt: Date | null;
  total: number;
  boardCardId: string | null;
}

/** The website ID check's slots (0132), in the storefront's order: the front of the ID, then the selfie with a
 *  handwritten note dated today. `id_back` and `face` are rows of the 0131 three-step flow, `id` of the 0128
 *  flow — kept as history. */
export type IdCheckSlot = 'id_front' | 'selfie' | 'id_back' | 'face' | 'id';
export const ID_CHECK_SLOTS: IdCheckSlot[] = ['id_front', 'selfie'];
const ALL_SLOTS: IdCheckSlot[] = [...ID_CHECK_SLOTS, 'id_back', 'face', 'id'];
export const ID_CHECK_SLOT_LABEL: Record<IdCheckSlot, string> = {
  id_front: 'ID front', selfie: 'Selfie with dated note',
  id_back: 'ID back (old flow)', face: 'Live check (old flow)', id: 'ID with face (old flow)',
};
/** The 0131 live check's three frames, as its rows list them (history only since 0132). */
export type IdCheckFrame = 'front' | 'left' | 'right';
export const ID_CHECK_FRAMES: IdCheckFrame[] = ['front', 'left', 'right'];
export const ID_CHECK_FRAME_LABEL: Record<IdCheckFrame, string> = { front: 'Facing the camera', left: 'Turned one way', right: 'Turned the other way' };

/** One upload of the website ID check, as id-check-photos serves it (a one-hour signed link; a 0131 live-check
 *  row's three frames each signed under `frames`). */
export interface IdCheckPhoto {
  id: string;
  slot: IdCheckSlot;
  /** A 0131 live-check row's frames (slot face, history); null on every other row. */
  frames: Array<{ frame: IdCheckFrame; url: string }> | null;
  url: string;
  verdict: 'pending' | 'accepted' | 'retry' | 'error';
  reason: string | null;
  observations: Record<string, unknown> | null;
  cycle: number;
  /** True for the cycle the pair is in now (an undo starts a new one). */
  currentCycle: boolean;
  uploadedAt: Date | null;
  judgedAt: Date | null;
  model: string | null;
  latencyMs: number | null;
  customerRef: string | null;
}

export type IdentityFilter = 'unverified' | 'needs_review' | 'verified' | 'all';
export type IdReviewDecision = 'verified' | 'rejected' | 'undo';

export const IDENTITIES_PAGE = 60;

const TABLE = 'customer_identities';
const PHOTOS_FN = '/.netlify/functions/id-check-photos';
const SOURCES: IdVerifiedSource[] = ['backfill', 'auto', 'dispatcher', 'ai'];
const STATUSES: IdCheckStatus[] = ['none', 'pending', 'retry', 'needs_review', 'rejected'];

/** The email half of the key: lower-cased and trimmed, '' when there is none (the SQL twin is customer_email_key). */
export function normalizeEmailKey(raw: string | null | undefined): string {
  return (raw ?? '').trim().toLowerCase();
}

/** The pair a card or order belongs to, or null when the phone is not a valid number (then there is no pair). */
export function identityKeyOf(phone: string | null | undefined, email?: string | null): { phone: string; email: string } | null {
  if (!isValidPhone(phone)) return null;
  return { phone: toE164(phone), email: normalizeEmailKey(email) };
}

export const identityKeyString = (k: { phone: string; email: string }): string => `${k.phone}|${k.email}`;

/** A row's key on a page: its id when the phone is masked (two masked numbers can read the same), else the pair. */
export const identityRowKey = (r: CustomerIdentity): string => (r.phoneMasked && r.ref ? `ref:${r.ref}` : identityKeyString(r));

/** The phone as the page shows it: the masked form as the server sent it, a full number formatted. */
export const displayPhone = (r: Pick<CustomerIdentity, 'phone' | 'phoneMasked'>): string => (r.phoneMasked ? r.phone : formatForDisplay(r.phone));

/** True when the database does not have the RPC yet (a migration not applied there): read the old way instead. */
function isMissingRpc(error: { code?: string; message?: string } | null | undefined): boolean {
  if (!error) return false;
  return error.code === 'PGRST202' || error.code === '42883' || /could not find the function/i.test(error.message || '');
}

const dateOf = (v: unknown): Date | null => {
  if (typeof v !== 'string' || !v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

export function identityFromRow(r: Record<string, unknown>): CustomerIdentity {
  const source = r.id_verified_source;
  const status = r.check_status;
  return {
    phone: String(r.phone ?? ''),
    ref: typeof r.id === 'string' && r.id ? r.id : null,
    phoneMasked: r.phone_masked === true,
    email: String(r.email ?? ''),
    name: String(r.name ?? ''),
    orderCount: Number(r.order_count ?? 0) || 0,
    firstOrderAt: dateOf(r.first_order_at),
    lastOrderAt: dateOf(r.last_order_at),
    isIdVerified: r.is_id_verified === true,
    idVerifiedAt: dateOf(r.id_verified_at),
    idVerifiedBy: typeof r.id_verified_by === 'string' ? r.id_verified_by : null,
    idVerifiedByName: typeof r.id_verified_by_name === 'string' ? r.id_verified_by_name : null,
    idVerifiedSource: SOURCES.includes(source as IdVerifiedSource) ? (source as IdVerifiedSource) : null,
    idVerifiedNote: typeof r.id_verified_note === 'string' ? r.id_verified_note : null,
    checkStatus: STATUSES.includes(status as IdCheckStatus) ? (status as IdCheckStatus) : 'none',
    checkAttempts: Number(r.check_attempts ?? 0) || 0,
    checkReason: typeof r.check_reason === 'string' && r.check_reason ? r.check_reason : null,
    checkCycle: Number(r.check_cycle ?? 1) || 1,
    checkUpdatedAt: dateOf(r.check_updated_at),
    updatedAt: dateOf(r.updated_at),
  };
}

/** PostgREST `or` filter for the page search: name and email by substring, phone by the digits typed
 *  (any formatting). Values are double-quoted so a comma or bracket cannot break the filter grammar. */
export function identitySearchFilter(query: string): string | null {
  const q = query.trim();
  if (!q) return null;
  const quote = (v: string) => `"%${v.replace(/["\\]/g, '')}%"`;
  const parts = [`name.ilike.${quote(q)}`, `email.ilike.${quote(q)}`];
  const digits = q.replace(/\D/g, '');
  if (digits) parts.push(`phone.ilike.${quote(digits)}`);
  return parts.join(',');
}

export interface IdentityPageQuery {
  filter: IdentityFilter;
  search?: string;
  offset?: number;
  limit?: number;
}

/** The chip's own filter on the request (nothing for 'all'). Untyped on purpose: the supabase builder's
 *  generic type blows past the compiler's depth limit when threaded through a helper. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyFilter(req: any, filter: IdentityFilter): any {
  if (filter === 'needs_review') return req.eq('check_status', 'needs_review');
  if (filter === 'all') return req;
  return req.eq('is_id_verified', filter === 'verified');
}

/** One page of the list: the chip (`filter`), the search, the offset; sorted by the latest order first
 *  (pairs with no order last), then name — the review queue by when the check changed, oldest first.
 *  `total` is the server's exact count for the chip + search. Since 0137 through id_identities_page /
 *  id_identities_total (the phone masked for everyone but the owner); the direct read on a database without. */
export async function fetchIdentitiesPage(q: IdentityPageQuery): Promise<{ rows: CustomerIdentity[]; total: number }> {
  const offset = q.offset ?? 0;
  const limit = q.limit ?? IDENTITIES_PAGE;
  const search = q.search || '';
  const [page, total] = await Promise.all([
    supabase().rpc('id_identities_page', { p_filter: q.filter, p_search: search, p_offset: offset, p_limit: limit }),
    supabase().rpc('id_identities_total', { p_filter: q.filter, p_search: search }),
  ]);
  if (isMissingRpc(page.error) || isMissingRpc(total.error)) return fetchIdentitiesPageDirect(q);
  if (page.error) throw new Error(`id_identities_page: ${page.error.message}`);
  if (total.error) throw new Error(`id_identities_total: ${total.error.message}`);
  const rows = ((page.data || []) as Record<string, unknown>[]).map((r) => identityFromRow(r));
  return { rows, total: typeof total.data === 'number' ? total.data : offset + rows.length };
}

/** The same page read from the table itself — a database without 0137 (or 0136). */
async function fetchIdentitiesPageDirect(q: IdentityPageQuery): Promise<{ rows: CustomerIdentity[]; total: number }> {
  const offset = q.offset ?? 0;
  const limit = q.limit ?? IDENTITIES_PAGE;
  let req = applyFilter(supabase().from(TABLE).select('*', { count: 'exact' }), q.filter);
  const filter = identitySearchFilter(q.search || '');
  if (filter) req = req.or(filter);
  req = q.filter === 'needs_review'
    ? req.order('check_updated_at', { ascending: true })
    : req.order('last_order_at', { ascending: false, nullsFirst: false }).order('name', { ascending: true });
  const { data, error, count } = await req.order('phone', { ascending: true }).range(offset, offset + limit - 1);
  if (error) throw new Error(`customer_identities: ${error.message}`);
  const rows = (data || []).map((r: Record<string, unknown>) => identityFromRow(r));
  return { rows, total: typeof count === 'number' ? count : offset + rows.length };
}

/** The four chip totals — id_identities_total per chip (0137); HEAD requests on a database without it. */
export async function countIdentities(): Promise<Record<IdentityFilter, number>> {
  const chips: IdentityFilter[] = ['unverified', 'needs_review', 'verified', 'all'];
  const totals = await Promise.all(chips.map((f) => supabase().rpc('id_identities_total', { p_filter: f, p_search: '' })));
  if (!totals.some((t) => isMissingRpc(t.error))) {
    for (const t of totals) if (t.error) throw new Error(`id_identities_total: ${t.error.message}`);
    const [u, n, v, a] = totals.map((t) => Number(t.data) || 0);
    return { unverified: u, needs_review: n, verified: v, all: a };
  }
  const head = (filter: IdentityFilter) => applyFilter(supabase().from(TABLE).select('phone', { count: 'exact', head: true }), filter);
  const [u, n, v, a] = await Promise.all([head('unverified'), head('needs_review'), head('verified'), head('all')]);
  for (const r of [u, n, v, a]) if (r.error) throw new Error(`customer_identities: ${r.error.message}`);
  return { unverified: u.count ?? 0, needs_review: n.count ?? 0, verified: v.count ?? 0, all: a.count ?? 0 };
}

/** How many website ID checks wait for a person — the sidebar badge of the review queue (RPC id_review_count,
 *  0136; the direct HEAD count on a database without it). */
export async function countIdReviews(): Promise<number> {
  const rpc = await supabase().rpc('id_review_count');
  if (!rpc.error) return Number(rpc.data) || 0;
  if (!isMissingRpc(rpc.error)) throw new Error(`id_review_count: ${rpc.error.message}`);
  const { count, error } = await supabase().from(TABLE).select('phone', { count: 'exact', head: true }).eq('check_status', 'needs_review');
  if (error) throw new Error(`customer_identities: ${error.message}`);
  return count ?? 0;
}

/** One page of the review queue (0136): the pairs waiting for a person, longest-waiting first, each with its id
 *  and the phone masked (in full for the owner only, 0137). On a database
 *  without 0136 it is the old direct read of the Needs review chip. */
export async function fetchReviewQueue(q: { offset?: number; limit?: number } = {}): Promise<{ rows: CustomerIdentity[]; total: number }> {
  const offset = q.offset ?? 0;
  const limit = q.limit ?? IDENTITIES_PAGE;
  const [page, count] = await Promise.all([
    supabase().rpc('id_review_queue', { p_offset: offset, p_limit: limit }),
    supabase().rpc('id_review_count'),
  ]);
  if (isMissingRpc(page.error) || isMissingRpc(count.error)) return fetchIdentitiesPageDirect({ filter: 'needs_review', offset, limit });
  if (page.error) throw new Error(`id_review_queue: ${page.error.message}`);
  if (count.error) throw new Error(`id_review_count: ${count.error.message}`);
  const rows = ((page.data || []) as Record<string, unknown>[]).map((r) => identityFromRow(r));
  return { rows, total: Number(count.data) || offset + rows.length };
}

const REVIEW_COUNT_EVERY_MS = 60_000;

/** The review-queue count for the sidebar, re-read every minute while `enabled`; 0 otherwise. */
export function useIdReviewCount(enabled: boolean): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) { setCount(0); return; }
    let live = true;
    const read = () => countIdReviews().then((n) => { if (live) setCount(n); }).catch(() => { /* the badge is a convenience */ });
    void read();
    const t = setInterval(read, REVIEW_COUNT_EVERY_MS);
    return () => { live = false; clearInterval(t); };
  }, [enabled]);
  return count;
}

/** The pairs of these phones (every email seen with them), keyed by `${phone}|${email}` — one call per board.
 *  Through customer_identities_for_phones (0136: the dispatch tier no longer reads the table; the board sends
 *  only the phones it already shows); the direct read on a database without it. */
export async function fetchIdentitiesByPhones(phones: string[]): Promise<Map<string, CustomerIdentity>> {
  const out = new Map<string, CustomerIdentity>();
  const wanted = Array.from(new Set(phones.filter(Boolean)));
  let direct = false;
  for (let i = 0; i < wanted.length; i += 200) {
    const chunk = wanted.slice(i, i + 200);
    let data: unknown[] | null = null;
    if (!direct) {
      const rpc = await supabase().rpc('customer_identities_for_phones', { p_phones: chunk });
      if (!rpc.error) data = (rpc.data || []) as unknown[];
      else if (isMissingRpc(rpc.error)) direct = true;
      else throw new Error(`customer_identities_for_phones: ${rpc.error.message}`);
    }
    if (direct) {
      const res = await supabase().from(TABLE).select('*').in('phone', chunk);
      if (res.error) throw new Error(`customer_identities: ${res.error.message}`);
      data = res.data || [];
    }
    for (const r of data || []) {
      const row = identityFromRow(r as Record<string, unknown>);
      out.set(identityKeyString(row), row);
    }
  }
  return out;
}

/** The pair's orders, newest first — matched in the database by the same key as the table (older orders hold
 *  the phone in other shapes, and the email as typed), under the caller's own rights on orders. */
export async function fetchIdentityOrders(phone: string, email: string): Promise<IdentityOrder[]> {
  const { data, error } = await supabase().rpc('customer_identity_orders', { p_phone: phone, p_email: email });
  if (error) throw new Error(`orders: ${error.message}`);
  return ((data || []) as Record<string, unknown>[]).map(identityOrderFromRow);
}

/** The same, by the pair's id (0136, the review queue): the orders without their phone or address. */
export async function fetchIdentityOrdersByRef(ref: string): Promise<IdentityOrder[]> {
  const { data, error } = await supabase().rpc('id_review_orders', { p_id: ref });
  if (error) throw new Error(`orders: ${error.message}`);
  return ((data || []) as Record<string, unknown>[]).map(identityOrderFromRow);
}

function identityOrderFromRow(r: Record<string, unknown>): IdentityOrder {
  return {
    id: String(r.id),
    orderNumber: String(r.order_number ?? ''),
    status: String(r.status ?? ''),
    createdAt: dateOf(r.created_at),
    deliveredAt: dateOf(r.delivered_at),
    total: (Number(r.cash_amount) || 0) + (Number(r.etransfer_amount) || 0),
    boardCardId: typeof r.board_card_id === 'string' ? r.board_card_id : null,
  };
}

/** The dispatcher's button: record (or clear) the ID check of a pair. Creates the pair's row when the card
 *  is not an order yet. Dispatch tier only (the database refuses others). Returns the row as it is now. */
export async function setIdVerified(phone: string, email: string, verified: boolean, note?: string): Promise<CustomerIdentity> {
  const { data, error } = await supabase().rpc('customer_identity_set_verified', {
    p_phone: phone, p_email: email, p_verified: verified, p_note: note ?? null,
  });
  if (error) throw new Error(error.message);
  return identityFromRow((data || {}) as Record<string, unknown>);
}

/** The person's decision on a website ID check (0128): verified · rejected · undo (a fresh cycle, so the
 *  customer may upload again). Dispatch tier only. Returns the row as it is now. */
export async function reviewIdCheck(phone: string, email: string, decision: IdReviewDecision, note?: string): Promise<CustomerIdentity> {
  const { data, error } = await supabase().rpc('customer_identity_review', {
    p_phone: phone, p_email: email, p_decision: decision, p_note: note ?? null,
  });
  if (error) throw new Error(error.message);
  return identityFromRow((data || {}) as Record<string, unknown>);
}

/** The number of a masked pair, asked only when someone presses "Open conversation" (0136): the Text Inbox's
 *  address is the number, and the Text Inbox shows it to the dispatch tier anyway. */
export async function fetchReviewPhone(ref: string): Promise<string> {
  const { data, error } = await supabase().rpc('id_review_phone', { p_id: ref });
  if (error) throw new Error(error.message);
  return String(data ?? '');
}

/** The same decision by the pair's id (0136, the review queue). Returns the row as the queue shows it (the
 *  phone masked for everyone but the owner). */
export async function reviewIdCheckByRef(ref: string, decision: IdReviewDecision, note?: string): Promise<CustomerIdentity> {
  const { data, error } = await supabase().rpc('id_review_decide', { p_id: ref, p_decision: decision, p_note: note ?? null });
  if (error) throw new Error(error.message);
  return identityFromRow((data || {}) as Record<string, unknown>);
}

/** The website ID check's photos of a pair, newest first, through the gated function (a super admin for any
 *  pair, the dispatch tier only for a pair pending review — a 403 otherwise). */
export async function fetchIdCheckPhotos(phone: string, email: string): Promise<IdCheckPhoto[]> {
  return readIdCheckPhotos(`phone=${encodeURIComponent(phone)}&email=${encodeURIComponent(email)}`);
}

/** The same by the pair's id (0136, the review queue): for everyone but the owner the photos come back
 *  as sealed id-check-photo-file links, which carry no number. */
export async function fetchIdCheckPhotosByRef(ref: string): Promise<IdCheckPhoto[]> {
  return readIdCheckPhotos(`ref=${encodeURIComponent(ref)}`);
}

async function readIdCheckPhotos(query: string): Promise<IdCheckPhoto[]> {
  const res = await fetch(`${PHOTOS_FN}?${query}`, {
    headers: { ...(await authHeader()) },
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail.error || `id-check-photos ${res.status}`);
  }
  const data = await res.json();
  return (Array.isArray(data.photos) ? data.photos : []).map((p: Record<string, unknown>) => ({
    id: String(p.id),
    slot: ALL_SLOTS.includes(p.slot as IdCheckSlot) ? (p.slot as IdCheckSlot) : 'id_front',
    frames: Array.isArray(p.frames)
      ? (p.frames as Array<Record<string, unknown>>)
        .filter((f) => ID_CHECK_FRAMES.includes(f.frame as IdCheckFrame) && typeof f.url === 'string')
        .map((f) => ({ frame: f.frame as IdCheckFrame, url: String(f.url) }))
      : null,
    url: String(p.url),
    verdict: (['pending', 'accepted', 'retry', 'error'].includes(String(p.verdict)) ? p.verdict : 'pending') as IdCheckPhoto['verdict'],
    reason: typeof p.reason === 'string' ? p.reason : null,
    observations: p.observations && typeof p.observations === 'object' ? (p.observations as Record<string, unknown>) : null,
    cycle: Number(p.cycle) || 1,
    currentCycle: p.currentCycle === true,
    uploadedAt: dateOf(p.uploadedAt),
    judgedAt: dateOf(p.judgedAt),
    model: typeof p.model === 'string' ? p.model : null,
    latencyMs: typeof p.latencyMs === 'number' ? p.latencyMs : null,
    customerRef: typeof p.customerRef === 'string' ? p.customerRef : null,
  }));
}

/** The refusal reasons as the storefront words them for the customer — for the panel, so support reads the
 *  same thing the customer saw — plus the hand-over reasons (ai_unavailable; the storefront's own codes). */
export const ID_CHECK_REASON_TEXT: Record<string, string> = {
  photo_blurry: 'Too blurry',
  id_unreadable: "Couldn't read the card",
  id_not_front: 'Not the front of the ID',
  id_not_accepted: 'ID not accepted (possible minor or tampered)',
  id_expired: 'ID looks expired',
  face_missing: 'No face in the frame',
  note_missing: 'No handwritten note with the date',
  note_date_wrong: 'The note is not dated today',
  faces_differ: 'Not the person on the ID',
  // the 0131 three-step flow, for its rows
  id_not_back: 'Not the back of the ID',
  id_mismatch: 'Front and back are not the same card',
  pose_wrong: 'Not facing the camera',
  head_turn_missing: 'Head not turned',
  live_check_failed: "Live check couldn't be completed",
  no_face_with_id: 'Face and ID not in the same picture (old flow)',
  ai_unavailable: 'The AI could not answer',
  storefront_review: 'Sent to review by the website',
  too_many_attempts: 'Too many tries on the website',
  no_camera: 'No camera on the website',
};
export const idCheckReasonText = (reason: string | null | undefined): string => (reason ? ID_CHECK_REASON_TEXT[reason] || reason.replace(/_/g, ' ') : '');
